/**
 * /oauth/plaid — Plaid OAuth redirect callback.
 *
 * When a user selects an OAuth-enabled bank (Chase, BofA, etc.) in Plaid Link,
 * Plaid redirects them to the bank's login, then redirects back here with
 * ?oauth_state_id=... in the URL.
 *
 * This page:
 *  1. Shows a loading indicator
 *  2. Retrieves the saved link_token from sessionStorage
 *  3. Re-initializes usePlaidLink with receivedRedirectUri
 *  4. Auto-opens Plaid Link to complete the OAuth flow
 *  5. On success, navigates to the accounts screen
 */

import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { View, Text, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { trpc } from '../../lib/trpc';
import { useColors } from '../../lib/colors';
import { getPendingPlaidOAuthState } from '../../lib/plaid-oauth';

import { usePlaidConnection } from '../../hooks/usePlaidConnection';

export default function PlaidOAuthCallback() {
  const router = useRouter();
  const [status, setStatus] = useState<'connecting' | 'success' | 'error'>('connecting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [returnPath, setReturnPath] = useState('/(app)/accounts');
  const c = useColors();

  const connectionsQuery = trpc.connections.list.useQuery(undefined, {
    enabled: status === 'success',
  });

  const { isOAuthRedirect } = usePlaidConnection({
    onSuccess: () => {
      setStatus('success');
      // Small delay to show success state, then navigate
      setTimeout(() => {
        router.replace(returnPath as any);
      }, 1500);
    },
    onError: (error) => {
      setStatus('error');
      setErrorMessage(error);
    },
  });

  useEffect(() => {
    if (Platform.OS === 'web') return;

    void (async () => {
      const pending = await getPendingPlaidOAuthState();
      if (pending?.returnPath) {
        setReturnPath(pending.returnPath);
      }
    })();
  }, []);

  // If we're not actually in an OAuth redirect, go back
  useEffect(() => {
    if (!isOAuthRedirect) {
      router.replace(returnPath as any);
    }
  }, [isOAuthRedirect, returnPath, router]);

  return (
    <View className="flex-1 items-center justify-center bg-white dark:bg-gray-900 gap-4 p-6">
      {status === 'connecting' && (
        <>
          <ActivityIndicator size="large" color={c.primary} />
          <Text className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Completing connection...
          </Text>
          <Text className="text-sm text-gray-500 text-center">
            Finishing your bank login. Please don't close this page.
          </Text>
        </>
      )}

      {status === 'success' && (
        <>
          <Text className="text-4xl">✓</Text>
          <Text className="text-lg font-semibold text-green-700">
            Account connected!
          </Text>
          <Text className="text-sm text-gray-500">
            Taking you to your accounts...
          </Text>
        </>
      )}

      {status === 'error' && (
        <>
          <Text className="text-4xl">✗</Text>
          <Text className="text-lg font-semibold text-red-600">
            Connection failed
          </Text>
          {errorMessage && (
            <Text className="text-sm text-gray-500 text-center">{errorMessage}</Text>
          )}
          <Text
            className="text-sm text-blue-600 underline"
            onPress={() => router.replace('/(app)/accounts')}
          >
            Go to Accounts
          </Text>
        </>
      )}
    </View>
  );
}
